'use client';

import React from 'react';
import classNames from 'classnames';
import ContentfulRichText from '@/components/ContentfulRichText/ContentfulRichText';
import AnimatedLink from '@/components/AnimatedLink/AnimatedLink';
import { analytics } from '@/utils/analytics';
import { composeImageUrl } from '@/utils/imageAsset';
import { DescriptionType, ImageType } from '@/utils/common.types';
import { bgColorMap } from '@/utils/styleHelpers';

interface FeatureProps {
  button?: {
    label: string;
    url: string;
    isOpenNewWindow: boolean;
  };
  description?: DescriptionType;
  linkStyle?: string;
  includeSegment?: boolean;
  image?: {
    desktop: ImageType;
    mobile: ImageType;
  };
  title?: string;
}

const Feature = ({
  button,
  description,
  linkStyle,
  includeSegment,
  image,
  title,
}: FeatureProps) => {
  return (
    <div className="mx-2.5 mb-2.5 mt-5 align-bottom">
      <div>
        {title && (
          <h3 className="text-[1.625rem] font-bold leading-[1.3] text-hbg-blue">
            {title}
          </h3>
        )}
        {image?.desktop?.url && (
          <picture>
            <source
              srcSet={composeImageUrl({
                image: image?.desktop,
                w: 375,
                h: 550,
              })}
              media="(min-width: 768px)"
              content={image?.desktop?.contentType}
            />
            <img
              srcSet={composeImageUrl({
                image: image?.mobile,
                w: 450,
                h: 550,
              })}
              alt={image?.mobile?.title}
              content={image?.mobile?.contentType}
              className="h-full w-full object-contain px-2.5 pb-[30px] pt-2.5 align-middle"
            />
          </picture>
        )}
        {description && (
          <span className="text-[16px] text-[#57595b]">
            <ContentfulRichText linkStyle={linkStyle} {...description} />
          </span>
        )}
        {button?.url && (
          <AnimatedLink
            buttonUrl={button?.url}
            buttonOpenNewWindow={button?.isOpenNewWindow}
            title={button?.label}
            onClick={() =>
              includeSegment &&
              analytics?.track('CTA Clicked', {
                isNav: true,
                type: 'text',
                text: button?.label || '',
              })
            }
          />
        )}
      </div>
    </div>
  );
};

interface GridProps {
  featureCards: FeatureProps[];
  includeCardShadow?: boolean;
  isImagesOnly?: boolean;
  backgroundColor?: string;
}

const FeatureCardGrid = ({
  featureCards,
  includeCardShadow = false,
  isImagesOnly = false,
  backgroundColor = 'default',
}: GridProps) => {
  return (
    <div
      className={classNames('mx-auto my-10 w-mobile max-w-standard', {
        [bgColorMap(backgroundColor)]: backgroundColor,
        ['shadow-component']: includeCardShadow,
      })}
    >
      <div className="grid grid-cols-1 grid-rows-2 md:grid-cols-2 md:grid-rows-1">
        {featureCards &&
          featureCards.map((feature, key) =>
            isImagesOnly ? (
              <div key={key} className="feature-image-container">
                <picture>
                  <source
                    srcSet={composeImageUrl({
                      image: feature?.image?.desktop,
                      w: 375,
                      h: 450,
                    })}
                    media="(min-width: 768px)"
                    content={feature?.image?.desktop?.contentType}
                  />
                  <img
                    srcSet={composeImageUrl({
                      image: feature?.image?.mobile,
                      w: 375,
                      h: 450,
                    })}
                    alt={feature?.image?.mobile?.title}
                    content={feature?.image?.mobile?.contentType}
                    className="h-full w-full object-contain align-bottom"
                  />
                </picture>
              </div>
            ) : (
              <Feature key={key} {...feature} />
            )
          )}
      </div>
    </div>
  );
};

export default FeatureCardGrid;
